import type { CashDocumentDto } from '../../CashDocuments/Types/cashDocumentTypes';

export enum InvoiceCashDocumentType {
  KP = 'kp',
  KW = 'kw',
}

export interface InvoiceCashDocumentLookup {
  id: number;
  type: InvoiceCashDocumentType;
  documentNumber: string;
  amount: number;
  currency: string;
  documentDate: string;
  personName?: string;
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const toStringOrEmpty = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const normalizeType = (value: unknown): InvoiceCashDocumentType => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return normalized === InvoiceCashDocumentType.KW
    ? InvoiceCashDocumentType.KW
    : InvoiceCashDocumentType.KP;
};

export const mapCashDocumentToInvoiceLookup = (
  document: CashDocumentDto | undefined | null
): InvoiceCashDocumentLookup | undefined => {
  if (!document) {
    return undefined;
  }

  return {
    id: toNumber((document as any).id) ?? 0,
    type: normalizeType((document as any).type),
    documentNumber: toStringOrEmpty((document as any).documentNumber),
    amount: toNumber((document as any).amount) ?? 0,
    currency: toStringOrEmpty((document as any).currency) || 'PLN',
    documentDate: toStringOrEmpty((document as any).documentDate),
    personName: toStringOrEmpty((document as any).personName) || undefined,
  };
};

export const getInvoiceCashDocumentTypeLabel = (type: InvoiceCashDocumentType): string =>
  type === InvoiceCashDocumentType.KP ? 'Kasa Przyjęła' : 'Kasa Wydała';

export const getInvoiceCashDocumentTypeColor = (
  type: InvoiceCashDocumentType
): 'success' | 'error' => (type === InvoiceCashDocumentType.KP ? 'success' : 'error');

export const formatInvoiceCashDocumentAmount = (amount: number, currency: string = 'PLN'): string =>
  new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency,
  }).format(amount);
